import { useRef, useState } from 'react';
import { FileText, Paperclip, X } from 'lucide-react';
import { api } from '@/lib/api';
import { fileUrl } from '@/lib/leave';
import { t } from '@/lib/i18n';
import { Button, ErrorBanner } from './ui';

export interface UploadedFile {
  id: string;
  filename: string;
}

const MAX = 5 * 1024 * 1024;

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** Attach a PDF or photo (medical report, policy document). Uploads right away and returns its id. */
export function FileUpload({
  kind,
  employeeId,
  value,
  onChange,
  label,
}: {
  kind: 'medical_report' | 'policy';
  employeeId?: string;
  value: UploadedFile | null;
  onChange: (f: UploadedFile | null) => void;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX) {
      setError(new Error(t('The file is larger than 5 MB. Take a smaller photo or scan.')));
      return;
    }
    setBusy(true);
    try {
      const content = await toBase64(file);
      const out = await api.post<UploadedFile>('/files', { kind, filename: file.name, content, ...(employeeId ? { employeeId } : {}) });
      onChange({ id: out.id, filename: out.filename });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
          <FileText className="h-4 w-4 shrink-0 text-emerald-700" />
          <a href={fileUrl(value.id)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-medium text-emerald-800 hover:underline">
            {value.filename}
          </a>
          <button type="button" className="rounded p-1 text-slate-500 hover:bg-emerald-100" onClick={() => onChange(null)} aria-label={t('Remove')}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <Button type="button" variant="secondary" icon={<Paperclip className="h-4 w-4" />} loading={busy} onClick={() => input.current?.click()}>
          {label ?? t('Attach file')}
        </Button>
      )}
      <p className="text-xs text-slate-500">{t('PDF, JPG or PNG, up to 5 MB.')}</p>
      <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <ErrorBanner error={error} />
    </div>
  );
}
